import React, { useState, useEffect } from 'react';

interface TypewriterProps {
  text: string;
  speed?: number;
  delay?: number;
  className?: string;
}

const Typewriter: React.FC<TypewriterProps> = ({ text, speed = 80, delay = 500, className = "" }) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [sparkles, setSparkles] = useState<{ id: number; x: number; y: number; size: number }[]>([]);
  
  const characters = text.split("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setStarted(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;

    if (wordIndex < characters.length) {
      const timeout = setTimeout(() => {
        setWordIndex(prev => prev + 1);
        
        // Add a sparkle occasionally
        if (Math.random() > 0.8) {
          const id = Date.now();
          setSparkles(prev => [...prev, { 
            id, 
            x: Math.random() * 40 - 20, 
            y: Math.random() * 20 - 10,
            size: Math.random() * 12 + 6
          }]);
          
          // Remove sparkle after animation
          setTimeout(() => {
            setSparkles(prev => prev.filter(s => s.id !== id));
          }, 1000);
        }
      }, speed);

      return () => clearTimeout(timeout);
    }
  }, [wordIndex, characters.length, speed, started]);

  const displayedText = characters.slice(0, wordIndex).join("");

  return (
    <div className="relative inline-block w-full">
      <p className={`${className} whitespace-pre-line`}>
        {displayedText}
        {wordIndex < characters.length && (
          <span className="relative inline-block">
            <span className="animate-pulse border-r-2 border-yellow-400 ml-1"></span>
            {sparkles.map(sparkle => (
              <span 
                key={sparkle.id}
                className="absolute pointer-events-none animate-ping text-yellow-300"
                style={{
                  left: `${sparkle.x}px`,
                  top: `${sparkle.y}px`,
                  fontSize: `${sparkle.size}px`
                }}
              >
                ✨
              </span>
            ))}
          </span>
        )}
      </p>
    </div>
  );
};

export default Typewriter;
